// client/src/modules/fleet/routes.tsx
import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { FormConsumerProvider } from '../../contexts/FormConsumerContext';
import { PageLayout } from '../../components/PageLayout';
import { lazyWithRetry } from '../../lib/lazyWithRetry';

// ---------------------------------------------------------------------------
// New unified Assets pages (Chunks 4-10)
// ---------------------------------------------------------------------------
const AssetsDashboard = lazyWithRetry(() => import('../../pages/Assets/AssetsDashboard'));
const AssetList = lazyWithRetry(() => import('../../pages/Assets/AssetList'));
const AssetDetail = lazyWithRetry(() => import('../../pages/Assets/AssetDetail'));
const ComplianceDashboard = lazyWithRetry(() => import('../../pages/Assets/ComplianceDashboard'));
const AssignmentsPage = lazyWithRetry(() => import('../../pages/Assets/AssignmentsPage'));
const CategorySettings = lazyWithRetry(() => import('../../pages/Assets/CategorySettings'));
const CategoryForm = lazyWithRetry(() => import('../../pages/Assets/CategoryForm'));
const TruckList = lazyWithRetry(() => import('../../pages/Assets/TruckList'));

// ---------------------------------------------------------------------------
// Legacy Fleet pages (kept for existing routes: inspections, tasks, drivers…)
// ---------------------------------------------------------------------------
const FleetDashboard = lazyWithRetry(() => import('../../pages/Fleet/FleetDashboard'));
const DriverList = lazyWithRetry(() => import('../../pages/Fleet/DriverList'));
const DriverDetail = lazyWithRetry(() => import('../../pages/Fleet/DriverDetail'));
const InspectionList = lazyWithRetry(() => import('../../pages/Fleet/InspectionList'));
const InspectionDetail = lazyWithRetry(() => import('../../pages/Fleet/InspectionDetail'));
const InspectionRequests = lazyWithRetry(() => import('../../pages/Fleet/InspectionRequests'));
const TaskQueue = lazyWithRetry(() => import('../../pages/Fleet/TaskQueue'));
const TaskDetail = lazyWithRetry(() => import('../../pages/Fleet/TaskDetail'));
const FleetReports = lazyWithRetry(() => import('../../pages/Fleet/FleetReports'));
const DataQuality = lazyWithRetry(() =>
  import('../../pages/Fleet/DataQuality').then((m) => ({ default: m.DataQuality }))
);
const PolicyReconciliation = lazyWithRetry(() => import('../../pages/Fleet/PolicyReconciliation'));
const TemplateListPage = lazyWithRetry(() => import('../../pages/Fleet/TemplateListPage'));
const DriverComplianceDashboard = lazyWithRetry(() => import('../../pages/Fleet/DriverComplianceDashboard'));
const InspectionSubmit = lazyWithRetry(() => import('../../pages/Fleet/InspectionSubmit'));

// Form consumer pages — shared with P8 Forms module
const FormSelector = lazyWithRetry(() => import('../../pages/Forms/FormSelector'));
const FormFiller = lazyWithRetry(() => import('../../pages/Forms/FormFiller'));
const FormSubmissionList = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionList'));
const FormSubmissionDetail = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionDetail'));
const FormSubmissionEdit = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionEdit'));
const FormReportPage = lazyWithRetry(() => import('../../pages/Forms/FormReportPage'));
const ModuleAgentFeed = lazyWithRetry(() =>
  import('../../components/shared/ModuleAgentFeed').then(m => ({ default: m.ModuleAgentFeed }))
);

/**
 * Wraps form consumer pages so navigation stays within /assets/forms/*
 */
function AssetsFormsConsumer({ children }: { children: React.ReactNode }) {
  return (
    <FormConsumerProvider basePath="/assets/forms" moduleLabel="Inspections" moduleId="P7">
      {children}
    </FormConsumerProvider>
  );
}

/**
 * Assets module routes - relative to /assets prefix
 *
 * Legacy /fleet/* paths are redirected to /assets/* via App.tsx.
 */
export const routes: RouteObject[] = [
  // ---------------------------------------------------------------------------
  // Dashboard (new unified index)
  // ---------------------------------------------------------------------------
  {
    index: true,
    element: <ProtectedRoute><AssetsDashboard /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Inventory (new universal asset list + detail)
  // ---------------------------------------------------------------------------
  {
    path: 'inventory',
    element: <ProtectedRoute><AssetList /></ProtectedRoute>,
  },
  {
    path: 'inventory/:id',
    element: <ProtectedRoute><AssetDetail /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Compliance dashboard (new cross-category view)
  // ---------------------------------------------------------------------------
  {
    path: 'compliance',
    element: <ProtectedRoute><ComplianceDashboard /></ProtectedRoute>,
  },
  // Legacy compliance/drivers sub-route
  {
    path: 'compliance/drivers',
    element: <ProtectedRoute><DriverComplianceDashboard /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Assignments
  // ---------------------------------------------------------------------------
  {
    path: 'assignments',
    element: <ProtectedRoute><AssignmentsPage /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Category settings
  // ---------------------------------------------------------------------------
  {
    path: 'settings',
    element: <ProtectedRoute><CategorySettings /></ProtectedRoute>,
  },
  {
    path: 'settings/category/new',
    element: <ProtectedRoute><CategoryForm /></ProtectedRoute>,
  },
  {
    path: 'settings/category/:id',
    element: <ProtectedRoute><CategoryForm /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Create new asset — redirect to inventory with create flow
  // ---------------------------------------------------------------------------
  {
    path: 'new',
    element: <Navigate to="/assets/inventory?create=true" replace />,
  },

  // ---------------------------------------------------------------------------
  // Direct asset detail — /assets/:id → new AssetDetail (used by list links)
  // ---------------------------------------------------------------------------
  {
    path: ':id',
    element: <ProtectedRoute><AssetDetail /></ProtectedRoute>,
  },
  // Edit route renders detail page (edit is handled via modal on detail page)
  {
    path: ':id/edit',
    element: <ProtectedRoute><AssetDetail /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Legacy redirects — keep old /assets/assets and /assets/vehicles working
  // ---------------------------------------------------------------------------
  {
    path: 'assets',
    element: <Navigate to="/assets/inventory" replace />,
  },
  {
    path: 'assets/:id',
    element: <ProtectedRoute><AssetDetail /></ProtectedRoute>,
  },
  {
    path: 'vehicles',
    element: <Navigate to="/assets/inventory" replace />,
  },
  {
    path: 'vehicles/:id',
    element: <ProtectedRoute><AssetDetail /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Trucks — dedicated fleet vehicle list with alerts
  // ---------------------------------------------------------------------------
  {
    path: 'trucks',
    element: <ProtectedRoute><TruckList /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Drivers (preserved)
  // ---------------------------------------------------------------------------
  {
    path: 'drivers',
    element: <ProtectedRoute><DriverList /></ProtectedRoute>,
  },
  {
    path: 'drivers/:id',
    element: <ProtectedRoute><DriverDetail /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Inspections (preserved)
  // ---------------------------------------------------------------------------
  {
    path: 'inspections/submit',
    element: <ProtectedRoute><InspectionSubmit /></ProtectedRoute>,
  },
  {
    path: 'inspections',
    element: <ProtectedRoute><InspectionList /></ProtectedRoute>,
  },
  {
    path: 'inspections/:id',
    element: <ProtectedRoute><InspectionDetail /></ProtectedRoute>,
  },
  {
    path: 'inspection-requests',
    element: <ProtectedRoute><InspectionRequests /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Tasks (preserved)
  // ---------------------------------------------------------------------------
  {
    path: 'tasks',
    element: <ProtectedRoute><TaskQueue /></ProtectedRoute>,
  },
  {
    path: 'tasks/:id',
    element: <ProtectedRoute><TaskDetail /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Reports & data tools (preserved)
  // ---------------------------------------------------------------------------
  {
    path: 'reports',
    element: <ProtectedRoute><FleetReports /></ProtectedRoute>,
  },
  {
    path: 'data-quality',
    element: <ProtectedRoute><DataQuality /></ProtectedRoute>,
  },
  {
    path: 'policy-reconciliation',
    element: <ProtectedRoute><PolicyReconciliation /></ProtectedRoute>,
  },
  {
    path: 'policy-reconciliation/:auditId',
    element: <ProtectedRoute><PolicyReconciliation /></ProtectedRoute>,
  },
  {
    path: 'templates',
    element: <ProtectedRoute><TemplateListPage /></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Form consumer routes — no P8 access required
  // ---------------------------------------------------------------------------
  {
    path: 'forms/fill',
    element: <ProtectedRoute><AssetsFormsConsumer><FormSelector /></AssetsFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/fill/:templateId',
    element: <ProtectedRoute><AssetsFormsConsumer><FormFiller /></AssetsFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions',
    element: <ProtectedRoute><AssetsFormsConsumer><FormSubmissionList /></AssetsFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId',
    element: <ProtectedRoute><AssetsFormsConsumer><FormSubmissionDetail /></AssetsFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId/edit',
    element: <ProtectedRoute><AssetsFormsConsumer><FormSubmissionEdit /></AssetsFormsConsumer></ProtectedRoute>,
  },
  {
    path: 'forms/submissions/:submissionId/report',
    element: <ProtectedRoute><AssetsFormsConsumer><FormReportPage /></AssetsFormsConsumer></ProtectedRoute>,
  },

  // ---------------------------------------------------------------------------
  // Agent Activity
  // ---------------------------------------------------------------------------
  {
    path: 'agent-activity',
    element: (
      <ProtectedRoute>
        <PageLayout>
          <h1 className="mb-6 text-xl font-bold text-slate-900">Agent Activity</h1>
          <ModuleAgentFeed moduleId="P7" />
        </PageLayout>
      </ProtectedRoute>
    ),
  },

  // ---------------------------------------------------------------------------
  // Catch-all — fall back to legacy FleetDashboard while migration completes
  // ---------------------------------------------------------------------------
  {
    path: '*',
    element: <ProtectedRoute><FleetDashboard /></ProtectedRoute>,
  },
];
